import React, { useState, useEffect, useCallback } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { QRCodeSVG } from 'qrcode.react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Alert,
    Button,
    CircularProgress,
    Divider,
    Paper,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    Tooltip,
} from '@mui/material';
import {
    ChevronRight,
    Lock,
    LockOpen,
    Add,
    Receipt,
    ExpandMore,
    ExpandLess,
    Warning,
    Delete,
    CheckCircle,
    LockReset,
    Search as SearchIcon,
    Clear as ClearIcon,
    PersonOff,
    Edit,
    Save,
    Sort as SortIcon,
    Close,
} from '@mui/icons-material';
import i18next from 'i18next';
import { invoiceAPI } from '../api';
import ScrollStrip from './common/ScrollStrip';
import AreaFilterRow from './common/AreaFilterRow';
import { sortAreasByRoute } from '../utils/routeOrder';
import ShareButtons from './ShareButtons';
import InvoiceViewDialog from './InvoiceViewDialog';
import { buildInvoiceShareText } from '../utils/invoiceShareMessage';

// Bill filter value for "customers in no area" (area filters here key on the area name).
const NO_AREA_FILTER = '_no_area';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Short month name in the app's language, for the strip boxes.
const monthShort = (m) => new Intl.DateTimeFormat(
    ({ te: 'te-IN', hi: 'hi-IN' }[(i18next.language || 'en').slice(0, 2)] || 'en-IN'),
    { month: 'short' }
).format(new Date(2000, (m || 1) - 1, 1));

// Period state classification
function getPeriodState(period) {
    if (!period.f_is_locked) return 'working';
    // Locked with no txns and no invoices but has adjustments = shell
    if (period.txn_count_subscription === 0 && period.txn_count_manual === 0
        && period.invoice_count === 0 && period.adjustment_count > 0) {
        return 'shell';
    }
    return 'closed';
}

function getPeriodChip(period) {
    const state = getPeriodState(period);
    if (state === 'working') return { label: 'Working', color: 'success', icon: <LockOpen sx={{ fontSize: '14px !important' }} /> };
    if (state === 'shell') return { label: 'Shell', color: 'warning', icon: <Warning sx={{ fontSize: '14px !important' }} /> };
    return { label: 'Closed', color: 'default', icon: <Lock sx={{ fontSize: '14px !important' }} /> };
}

// Find index of the working (unlocked) period, or fall back to latest
function findWorkingIdx(periods) {
    if (periods.length === 0) return 0;
    const idx = periods.findIndex(p => !p.f_is_locked);
    return idx >= 0 ? idx : periods.length - 1;
}

const BillingPage = ({ businessId, business = {}, customers = [], products = [], areas = [] }) => {
    const [periods, setPeriods] = useState([]);           // sorted asc
    const [periodIdx, setPeriodIdx] = useState(null);     // index into periods[]
    const [setupLoading, setSetupLoading] = useState(true);
    const [periodLoading, setPeriodLoading] = useState(false);

    // Period-level data
    const [manualTxns, setManualTxns] = useState([]);

    const [subSummary, setSubSummary] = useState(null);   // { count, total }
    const [subTxns, setSubTxns] = useState([]);
    const [subDialogOpen, setSubDialogOpen] = useState(false);
    const [allInvoices, setAllInvoices] = useState([]);
    const [viewInvoiceId, setViewInvoiceId] = useState(null);  // shared InvoiceViewDialog
    const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);

    // Dialogs
    const [billSearch, setBillSearch] = useState('');
    const [genLoading, setGenLoading] = useState(false);
    const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
    const [lockLoading, setLockLoading] = useState(false);
    const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
    const [unlockLoading, setUnlockLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // txn id to delete
    const [earlierLoading, setEarlierLoading] = useState(false);
    const [earlierConfirmOpen, setEarlierConfirmOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [notesEditing, setNotesEditing] = useState(false);
    const [notesDraft, setNotesDraft] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
        const handleDeleteEarliest = async () => {
            setDeleteLoading(true);
            setError('');
            try {
                const period = periods[0];
                await invoiceAPI.deleteEarliestPeriod(businessId, period.f_id);
                const sorted = await reloadPeriods();
                setPeriodIdx(0);
                setSuccess('Month deleted.');
            } catch (e) {
                setError('Could not delete this month.');
            } finally {
                setDeleteLoading(false);
                setDeleteConfirmOpen(false);
            }
        };
    const [continueLoading, setContinueLoading] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(true);
    const [billSort, setBillSort] = useState('name');       // 'name' | 'amount_desc' | 'amount_asc' | 'due_desc' | 'due_asc' | 'status'
    const [billAreaFilter, setBillAreaFilter] = useState(''); // '' = all, else area name
    const [billVisibleCount, setBillVisibleCount] = useState(50);


    // Invoice adjustments
    const [invoiceAdjustments, setInvoiceAdjustments] = useState({});  // { [invoiceId]: adjustment[] }
    const [adjFormOpen, setAdjFormOpen] = useState(null);              // invoice id whose form is open
    const [adjFormData, setAdjFormData] = useState({ type: 'Discount', description: '', amount: '' });
    const [adjLoading, setAdjLoading] = useState(false);
    const adjTypeLabels = { Discount: 'Discount', OldDues: 'Old Dues', OtherCharges: 'Other Charges', DeliveryFee: 'Delivery Fee', ServiceFee: 'Service Fee', LateFee: 'Late Fee', Other: 'Other', OrderDiscount: 'Order Discount' };

    // Card-level share loading: { [invoiceId_action]: true }

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // ── Sorting helper ──
    const sortPeriods = (list) =>
        [...list].sort((a, b) =>
            a.f_period_year !== b.f_period_year
                ? a.f_period_year - b.f_period_year
                : a.f_period_month - b.f_period_month
        );

    // ── Reload full period list from server ──
    const reloadPeriods = useCallback(async (selectPeriodId) => {
        const resp = await invoiceAPI.listInvoicePeriods(businessId);
        const sorted = sortPeriods(resp.data || []);
        setPeriods(sorted);
        if (selectPeriodId) {
            const idx = sorted.findIndex(p => p.f_id === selectPeriodId);
            if (idx >= 0) setPeriodIdx(idx);
        }
        return sorted;
    }, [businessId]);

    // ── Setup: load periods (don't auto-create; let user choose) ──
    const setup = useCallback(async () => {
        if (!businessId) return;
        setSetupLoading(true);
        setError('');
        try {
            const sorted = await reloadPeriods();
            if (sorted.length === 0) {
                // Very first time — create the current month
                const workingResp = await invoiceAPI.ensureWorkingPeriod(businessId);
                const workingPeriod = workingResp.data;
                const sorted2 = await reloadPeriods(workingPeriod.f_id);
                setPeriodIdx(findWorkingIdx(sorted2));
            } else {
                // Periods exist — navigate to working period or latest
                setPeriodIdx(findWorkingIdx(sorted));
            }
        } catch (e) {
            setError('Could not set up billing. Please refresh.');
        } finally {
            setSetupLoading(false);
        }
    }, [businessId, reloadPeriods]);

    useEffect(() => {
        setup();
    }, [setup]);

    // ── Reload period-level data when period changes ──
    const loadPeriodData = useCallback(async ({ autoRefresh = false } = {}) => {
        if (periodIdx === null || !periods[periodIdx]) return;
        const period = periods[periodIdx];
        const periodIsWorking = !period.f_is_locked && period.f_period_year && period.f_period_month;

        // Auto-refresh subscription txns once per day on first visit (working period only)
        if (autoRefresh && periodIsWorking) {
            const storageKey = `sub_refresh_${businessId}_${period.f_id}_${new Date().toISOString().slice(0, 10)}`;
            if (!localStorage.getItem(storageKey)) {
                try {
                    await invoiceAPI.generateSubscriptionTransactions(businessId, period.f_id);
                    localStorage.setItem(storageKey, '1');
                } catch (_) {
                    // non-fatal — proceed to load data anyway
                }
            }
        }

        setPeriodLoading(true);
        setError('');
        setManualTxns([]);
        setSubSummary(null);
        setAllInvoices([]);
        try {
            const [manualResp, subSummaryResp, invoiceResp] = await Promise.all([
                invoiceAPI.listTransactions(businessId, {
                    invoice_period_id: period.f_id,
                    origin: 'Manual',
                    per_page: 100,
                }),
                invoiceAPI.getSubscriptionSummary(businessId, period.f_id),
                invoiceAPI.listInvoices(businessId, {
                    invoice_period_id: period.f_id,
                }),
            ]);

            setManualTxns(manualResp.data?.transactions || []);

            const summaryData = subSummaryResp.data || {};
            setSubSummary({
                total: parseFloat(summaryData.total_amount || 0),
                by_product: summaryData.by_product || [],
                extra_by_product: summaryData.extra_by_product || [],
                less_by_product: summaryData.less_by_product || [],
                absent_by_product: summaryData.absent_by_product || [],
                absent_customer_count: summaryData.absent_customer_count || 0,
            });

            const invoices = invoiceResp.data || [];
            setAllInvoices(invoices);

            // Auto-recalculate if bill is stale (working period with existing invoices only)
            if (periodIsWorking && invoices.length > 0) {
                try {
                    const staleResp = await invoiceAPI.checkPeriodStale(businessId, period.f_id);
                    if (staleResp.data?.is_stale) {
                        await invoiceAPI.generateSubscriptionTransactions(businessId, period.f_id);
                        await invoiceAPI.generateInvoices(businessId, {
                            f_invoice_period_id: period.f_id,
                            replace_existing: true,
                            regenerate_aggregates: true,
                        });
                        const [freshInvoiceResp, freshSummaryResp] = await Promise.all([
                            invoiceAPI.listInvoices(businessId, { invoice_period_id: period.f_id }),
                            invoiceAPI.getSubscriptionSummary(businessId, period.f_id),
                        ]);
                        setAllInvoices(freshInvoiceResp.data || []);
                        const freshSummary = freshSummaryResp.data || {};
                        setSubSummary({
                            total: parseFloat(freshSummary.total_amount || 0),
                            by_product: freshSummary.by_product || [],
                            extra_by_product: freshSummary.extra_by_product || [],
                            less_by_product: freshSummary.less_by_product || [],
                            absent_by_product: freshSummary.absent_by_product || [],
                            absent_customer_count: freshSummary.absent_customer_count || 0,
                        });
                        setSuccess('Bill updated automatically.');
                    }
                } catch (_) {
                    // Stale check failure is non-fatal
                }
            }
        } catch (e) {
            setError('Failed to load billing data.');
        } finally {
            setPeriodLoading(false);
        }
    }, [businessId, periodIdx, periods]);

    const currentPeriod = periods[periodIdx] ?? null;
    const currentPeriodId = currentPeriod?.f_id ?? null;

    useEffect(() => {
        if (!setupLoading && currentPeriodId !== null) loadPeriodData({ autoRefresh: true });
    }, [currentPeriodId, setupLoading]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setBillVisibleCount(50); }, [billSearch, billAreaFilter, billSort]);
    const periodState = currentPeriod ? getPeriodState(currentPeriod) : null;
    const isWorking = periodState === 'working';
    const isClosed = periodState === 'closed';
    const isShell = periodState === 'shell';

    // ── Helpers ──
    const customerName = (id) => customers.find(c => c.f_id === id)?.f_name || `Cust #${id}`;
    const productName = (id) => products.find(p => p.f_id === id)?.f_name
        || `Item #${id}`;

    const formatDate = (s) => {
        if (!s) return '';
        const d = new Date(s);
        return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
    };

    const periodLabel = (p) => `${MONTH_NAMES[p.f_period_month - 1]} ${p.f_period_year}`;

    const getPaymentColor = (status) => {
        if (status === 'paid') return 'success';
        if (status === 'partial') return 'warning';
        return 'error';
    };

    // Future periods (after current) for cascade warning
    const futurePeriods = currentPeriod
        ? periods.filter(p =>
            p.f_period_year > currentPeriod.f_period_year
            || (p.f_period_year === currentPeriod.f_period_year && p.f_period_month > currentPeriod.f_period_month)
          )
        : [];

    // ── Handlers ──



    const handleDeleteTxn = async (txnId) => {
        try {
            await invoiceAPI.deleteTransaction(businessId, txnId);
            setDeleteConfirm(null);
            loadPeriodData();
        } catch (e) {
            setError('Could not delete. Please try again.');
        }
    };

    const handleSaveNotes = async () => {
        if (!currentPeriod) return;
        setNotesSaving(true);
        try {
            await invoiceAPI.updatePeriodNotes(businessId, currentPeriod.f_id, { f_notes: notesDraft });
            // Update the local period list so the note is reflected immediately
            setPeriods(prev => prev.map(p =>
                p.f_id === currentPeriod.f_id ? { ...p, f_notes: notesDraft || null } : p
            ));
            setNotesEditing(false);
        } catch (e) {
            setError('Could not save message.');
        } finally {
            setNotesSaving(false);
        }
    };

    const handleGenerateInvoice = async (replace = false) => {
        setGenLoading(true);
        try {
            await invoiceAPI.generateSubscriptionTransactions(businessId, currentPeriod.f_id);
            const genRes = await invoiceAPI.generateInvoices(businessId, {
                f_invoice_period_id: currentPeriod.f_id,
                replace_existing: replace,
                regenerate_aggregates: true,
            });
            const count = genRes.data?.invoices_created?.length ?? 0;
            setSuccess(count > 0 ? `${count} bill${count !== 1 ? 's' : ''} created!` : 'No activity found for this month. No bills generated.');
            loadPeriodData();
        } catch (e) {
            setError('Could not create bill. Please try again.');
        } finally {
            setGenLoading(false);
        }
    };

    // ── Invoice adjustment handlers ──
    const loadAdjustments = async (invoiceId) => {
        try {
            const resp = await invoiceAPI.listInvoiceAdjustments(businessId, invoiceId);
            setInvoiceAdjustments(prev => ({ ...prev, [invoiceId]: resp.data || [] }));
        } catch (e) {
            // silently ignore
        }
    };

    const handleExpandInvoice = (invoiceId) => {
        const newId = expandedInvoiceId === invoiceId ? null : invoiceId;
        setExpandedInvoiceId(newId);
        setAdjFormOpen(null);
        if (newId) loadAdjustments(newId);
    };

    const handleAddAdjustment = async (invoiceId) => {
        if (!adjFormData.amount || isNaN(parseFloat(adjFormData.amount))) return;
        setAdjLoading(true);
        try {
            await invoiceAPI.addInvoiceAdjustment(businessId, invoiceId, {
                f_adjustment_type: adjFormData.type,
                f_adjustment_description: adjFormData.description || '',
                f_adjustment_amount: parseFloat(adjFormData.amount),
            });
            setAdjFormData({ type: 'Discount', description: '', amount: '' });
            setAdjFormOpen(null);
            await loadAdjustments(invoiceId);
            loadPeriodData();  // refresh totals
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not add adjustment.');
        } finally {
            setAdjLoading(false);
        }
    };

    const handleDeleteAdjustment = async (invoiceId, adjustmentId) => {
        try {
            await invoiceAPI.deleteInvoiceAdjustment(businessId, invoiceId, adjustmentId);
            await loadAdjustments(invoiceId);
            loadPeriodData();
        } catch (e) {
            setError('Could not remove adjustment.');
        }
    };

    // ── Card-level share helpers ──
    const getCardShareData = (inv) => {
        const cust = customers.find(c => c.f_id === inv.f_business_customer_id) || {};
        const custPhone = (cust.f_mobile || '').replace(/\D/g, '');
        const waPhone = custPhone.length === 10 ? `91${custPhone}` : custPhone;
        const prevDue = parseFloat(inv.f_previous_due || 0);
        const grandTotal = parseFloat(inv.f_total_amount) + prevDue;
        const vendorName = business.f_name || 'Your Vendor';
        const period = inv.invoice_period_display || '';
        const waMsg = buildInvoiceShareText(inv, inv.customer_name || cust.f_name);
        const noPhone = cust.f_has_mobile === false;   // no phone: print or hand the bill over
        return { custPhone, waPhone, waMsg, grandTotal, vendorName, period, noPhone };
    };

    const handleLock = async () => {
        setLockLoading(true);
        try {
            await invoiceAPI.lockInvoicePeriod(businessId, currentPeriod.f_id);
            setLockConfirmOpen(false);
            // Stay on this period so invoices can be shared — don't navigate away
            await reloadPeriods(currentPeriod.f_id);
            setSuccess('Month closed. Share the invoices with your customers below.');
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(detail || 'Could not close the month. Please try again.');
            setLockConfirmOpen(false);
        } finally {
            setLockLoading(false);
        }
    };

    const handleUnlock = async () => {
        setUnlockLoading(true);
        try {
            await invoiceAPI.unlockInvoicePeriod(businessId, currentPeriod.f_id);
            setUnlockConfirmOpen(false);
            await reloadPeriods(currentPeriod.f_id);
            
            // Regenerate invoices after reopening
            await invoiceAPI.generateSubscriptionTransactions(businessId, currentPeriod.f_id);
            await invoiceAPI.generateInvoices(businessId, {
                f_invoice_period_id: currentPeriod.f_id,
                replace_existing: true,
                regenerate_aggregates: true,
            });
            
            setSuccess(`${periodLabel(currentPeriod)} reopened and bills recalculated.`);
            loadPeriodData();
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(detail || 'Could not reopen the month. Please try again.');
            setUnlockConfirmOpen(false);
        } finally {
            setUnlockLoading(false);
        }
    };

    const handleCreateEarlier = async () => {
        setEarlierLoading(true);
        setError('');
        try {
            const resp = await invoiceAPI.createEarlierPeriod(businessId);
            const newPeriod = resp.data;
            const sorted = await reloadPeriods(newPeriod.f_id);
            const idx = sorted.findIndex(p => p.f_id === newPeriod.f_id);
            if (idx >= 0) setPeriodIdx(idx);
            setSuccess(`${MONTH_NAMES[newPeriod.f_period_month - 1]} ${newPeriod.f_period_year} created as working period.`);
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(detail || 'Could not create earlier month. Please try again.');
        } finally {
            setEarlierLoading(false);
            setEarlierConfirmOpen(false);
        }
    };

    const handleContinueForward = async () => {
        setContinueLoading(true);
        setError('');
        try {
            const workingResp = await invoiceAPI.ensureWorkingPeriod(businessId);
            const workingPeriod = workingResp.data;
            const sorted = await reloadPeriods(workingPeriod.f_id);
            setPeriodIdx(findWorkingIdx(sorted));
            setSuccess(`${MONTH_NAMES[workingPeriod.f_period_month - 1]} ${workingPeriod.f_period_year} is now open.`);
        } catch (e) {
            setError('Could not create next month. Please try again.');
        } finally {
            setContinueLoading(false);
        }
    };

    // ── Render ──
    if (setupLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Setting up billing...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 10 }}>
            {/* Month navigation — a strip of the months that exist, instead of ‹ › chevrons
                that say nothing about what they change. Only real periods appear, so a vendor
                cannot land on a month the business has no bills for. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {currentPeriod ? periodLabel(currentPeriod) : '—'}
                </Typography>
                {currentPeriod && (() => {
                    const chip = getPeriodChip(currentPeriod);
                    return (
                        <Chip size="small" label={chip.label} color={chip.color} icon={chip.icon}
                            sx={{ height: 20, fontSize: '0.7rem' }} />
                    );
                })()}
            </Box>
            <Box sx={{ mb: 1.5 }}>
                <ScrollStrip
                    items={periods.map((p, i) => ({
                        key: p.f_id,
                        top: monthShort(p.f_period_month),
                        bottom: `'${String(p.f_period_year).slice(-2)}`,
                        // Outline the working month so it stays findable after scrolling away.
                        accent: !p.f_is_locked,
                        idx: i,
                    }))}
                    selectedKey={currentPeriod?.f_id}
                    onSelect={(_key, it) => setPeriodIdx(it.idx)}
                />
            </Box>

            {/* Add Earlier Month & Delete This Month — visible when viewing the earliest period */}
            {periods.length > 0 && periodIdx === 0 && (
                <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        startIcon={earlierLoading ? <CircularProgress size={16} /> : <Add />}
                        onClick={() => setEarlierConfirmOpen(true)}
                        disabled={earlierLoading}
                    >
                        {(() => {
                            const earliest = periods[0];
                            const pm = earliest.f_period_month === 1 ? 12 : earliest.f_period_month - 1;
                            const py = earliest.f_period_month === 1 ? earliest.f_period_year - 1 : earliest.f_period_year;
                            return `Add ${MONTH_NAMES[pm - 1]} ${py}`;
                        })()}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        size="small"
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <LockOpen />}
                        onClick={() => setDeleteConfirmOpen(true)}
                        disabled={deleteLoading || periods.length <= 1}
                        title={periods.length <= 1 ? 'Cannot delete the only remaining month' : ''}
                    >
                        Delete This Month
                    </Button>
                </Box>
                <Dialog open={earlierConfirmOpen} onClose={() => setEarlierConfirmOpen(false)}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning color="warning" /> Add Earlier Month
                    </DialogTitle>
                    <DialogContent>
                        <Alert severity="warning">
                            <b>All future months will be deleted.</b><br />
                            You will need to generate invoices again for each month.<br />
                            Are you sure you want to continue?
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEarlierConfirmOpen(false)} disabled={earlierLoading}>Cancel</Button>
                        <Button onClick={handleCreateEarlier} color="warning" variant="contained" disabled={earlierLoading} autoFocus>
                            Yes, delete future months
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning color="error" /> Delete This Month
                    </DialogTitle>
                    <DialogContent>
                        <Alert severity="error">
                            <b>This month and all its data will be permanently deleted.</b><br />
                            This action cannot be undone.<br />
                            Are you sure you want to continue?
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
                        <Button onClick={handleDeleteEarliest} color="error" variant="contained" disabled={deleteLoading} autoFocus>
                            Yes, delete this month
                        </Button>
                    </DialogActions>
                </Dialog>
                </>
            )}

            {/* Continue Forward — visible when viewing the latest closed period */}
            {isClosed && (periodIdx === periods.length - 1 || !periods[periodIdx + 1]?.f_is_locked) && (
                <>
                    {(() => {
                        const total = allInvoices.length;
                        const sent = allInvoices.filter(inv => (inv.f_sent || 0) > 0).length;
                        const unsent = total - sent;
                        if (total === 0) return null;
                        if (unsent === 0) return (
                            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 1.5 }}>
                                All {total} invoice{total !== 1 ? 's' : ''} shared. Ready to start the next month.
                            </Alert>
                        );
                        return (
                            <Alert severity="warning" sx={{ mb: 1.5 }}>
                                {sent > 0
                                    ? `${unsent} of ${total} invoice${total !== 1 ? 's' : ''} not yet shared. Share them via WhatsApp before continuing.`
                                    : `Share the ${total} invoice${total !== 1 ? 's' : ''} with your customers via WhatsApp before starting the next month.`
                                }
                            </Alert>
                        );
                    })()}
                    {(() => {
                        const nextPeriod = periods[periodIdx + 1];
                        const cur = periods[periodIdx];
                        const nm = cur.f_period_month === 12 ? 1 : cur.f_period_month + 1;
                        const ny = cur.f_period_month === 12 ? cur.f_period_year + 1 : cur.f_period_year;
                        const _now = new Date();
                        const nextIsInFuture = ny > _now.getFullYear() || (ny === _now.getFullYear() && nm > _now.getMonth() + 1);
                        if (!nextPeriod && nextIsInFuture) {
                            return (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {MONTH_NAMES[nm - 1]} {ny} hasn't started yet. You can open it once the month begins.
                                </Alert>
                            );
                        }
                        return (
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={continueLoading ? <CircularProgress size={16} color="inherit" /> : <ChevronRight />}
                                onClick={handleContinueForward}
                                disabled={continueLoading}
                                sx={{ mb: 2 }}
                            >
                                {nextPeriod ? `Go to ${MONTH_NAMES[nextPeriod.f_period_month - 1]} ${nextPeriod.f_period_year}` : `Continue to ${MONTH_NAMES[nm - 1]} ${ny}`}
                            </Button>
                        );
                    })()}
                </>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {periodLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : !currentPeriod ? (
                <Alert severity="info">No billing months found.</Alert>
            ) : isShell ? (
                /* ── Shell period: only adjustments survive ── */
                <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This month was wiped when an earlier month was reopened.
                        Only adjustments are retained.
                    </Alert>
                    {currentPeriod.adjustment_count > 0 && (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    {currentPeriod.adjustment_count} adjustment(s) retained in this month.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            ) : (
                <>
                    {/* ── Reopen banner for closed periods ── */}
                    {isClosed && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                            icon={<Lock />}
                            action={
                                <Button
                                    size="small"
                                    color="inherit"
                                    startIcon={<LockReset />}
                                    onClick={() => setUnlockConfirmOpen(true)}
                                >
                                    Reopen
                                </Button>
                            }
                        >
                            This month is closed. Reopen to make changes.
                        </Alert>
                    )}

                    {/* ── Deliveries summary (regular + extra + less + absent) ── */}
                    {(() => {
                        // Regular subscription deliveries (pre-aggregated from backend)
                        const subByProduct = {};
                        for (const p of subSummary?.by_product || []) {
                            subByProduct[p.product_name] = {
                                qty: parseFloat(p.total_qty) || 0,
                                amount: parseFloat(p.total_amount) || 0,
                            };
                        }
                        const subTotal = subSummary?.total || 0;

                        // Pre-aggregated from backend — no client-side grouping needed
                        const byExtra = Object.fromEntries(
                            (subSummary?.extra_by_product || []).map(p => [p.product_name, { qty: parseFloat(p.total_qty), amount: parseFloat(p.total_amount) }])
                        );
                        const byLess = Object.fromEntries(
                            (subSummary?.less_by_product || []).map(p => [p.product_name, { qty: parseFloat(p.total_qty), amount: parseFloat(p.total_amount) }])
                        );
                        const byAbsent = Object.fromEntries(
                            (subSummary?.absent_by_product || []).map(p => [p.product_name, { qty: parseFloat(p.total_qty), amount: parseFloat(p.total_amount) }])
                        );
                        const absentCount = subSummary?.absent_customer_count || 0;

                        const manualTotal = manualTxns.reduce((s, t) => s + (parseFloat(t.f_total_sale_price_after_discount) || 0), 0);
                        const extraDailyOpsTotal = Object.values(byExtra).reduce((s, v) => s + v.amount, 0);
                        const lessTotal = Object.values(byLess).reduce((s, v) => s + v.amount, 0);
                        const absentTotal = Object.values(byAbsent).reduce((s, v) => s + v.amount, 0);
                        const grandTotal = subTotal + manualTotal + extraDailyOpsTotal - lessTotal - absentTotal;
                        const totalQty = Object.values(subByProduct).reduce((s, v) => s + v.qty, 0);
                        const billCount = allInvoices.length;
                        const totalAdjustments = allInvoices.reduce((s, inv) => s + parseFloat(inv.f_adjustments_total || 0), 0);
                        const totalPreviousDues = allInvoices.reduce((s, inv) => s + parseFloat(inv.f_previous_due || 0), 0);
                        const totalBillAmount = allInvoices.reduce((s, inv) => s + parseFloat(inv.f_total_amount || 0), 0);
                        return (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.default' }}>
                                {/* Header */}
                                <Box
                                    display="flex" justifyContent="space-between" alignItems="center"
                                    px={1.5} py={0.75}
                                    onClick={() => setSummaryOpen(o => !o)}
                                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Deliveries · {totalQty} items
                                        </Typography>

                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                            ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{summaryOpen ? '▲' : '▼'}</Typography>
                                    </Box>
                                </Box>
                                <Collapse in={summaryOpen}>
                                    <Divider />
                                    <Box px={1.5} py={1}>
                                        {/* Regular delivery rows */}
                                        {Object.entries(subByProduct).map(([name, { qty, amount }]) => (
                                            <Box key={name} display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                <Typography variant="body2" color="text.primary">
                                                    {name} <Typography component="span" variant="caption" color="text.secondary">× {qty}</Typography>
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    ₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {Object.keys(subByProduct).length === 0 && (
                                            <Typography variant="caption" color="text.secondary">No regular deliveries recorded</Typography>
                                        )}
                                        {/* Daily-ops extra rows (grouped by product) */}
                                        {Object.entries(byExtra).map(([name, { qty, amount }]) => (
                                            <Box key={`extra-${name}`} display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                <Typography variant="body2" color="success.main">
                                                    {name} <Typography component="span" variant="caption" color="success.light">+{qty} extra</Typography>
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500} color="success.main">
                                                    +₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {/* Billing-page manual extra rows (individual, with delete) */}
                                        {manualTxns.map((txn) => (
                                            <Box key={txn.f_id} display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                <Typography variant="body2" color="success.main">
                                                    {productName(txn.f_business_product_id)}
                                                    <Typography component="span" variant="caption" color="success.light"> +{txn.f_quantity} extra</Typography>
                                                    <Typography component="span" variant="caption" color="text.secondary"> · {customerName(txn.f_business_customer_id)} · {formatDate(txn.f_time)}</Typography>
                                                </Typography>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Typography variant="body2" fontWeight={500} color="success.main">
                                                        +₹{parseFloat(txn.f_total_sale_price_after_discount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                    </Typography>
                                                    {isWorking && (
                                                        <IconButton size="small" onClick={() => setDeleteConfirm(txn.f_id)}>
                                                            <Delete fontSize="small" color="action" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                        {/* Less rows (grouped by product) */}
                                        {Object.entries(byLess).map(([name, { qty, amount }]) => (
                                            <Box key={`less-${name}`} display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                <Typography variant="body2" color="warning.dark">
                                                    {name} <Typography component="span" variant="caption" color="warning.main">{qty} less</Typography>
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500} color="warning.dark">
                                                    -₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {/* Absent rows (grouped by product) — negative deductions matching absent subscription days */}
                                        {Object.entries(byAbsent).map(([name, { qty, amount }]) => (
                                            <Box key={`absent-${name}`} display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                <Typography variant="body2" color="error.main">
                                                    {name} <Typography component="span" variant="caption" color="error.light">{qty} absent</Typography>
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500} color="error.main">
                                                    -₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {/* Absent customer count chip */}
                                        {absentCount > 0 && (
                                            <Box mt={0.75}>
                                                <Chip icon={<PersonOff fontSize="small" />} label={`${absentCount} customer${absentCount > 1 ? 's' : ''} absent`} color="error" size="small" variant="outlined" />
                                            </Box>
                                        )}

                                        {/* Bill summary stats */}
                                        {billCount > 0 && (
                                            <>
                                                <Divider sx={{ my: 1 }} />
                                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                                                        Bills generated
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {billCount}
                                                    </Typography>
                                                </Box>
                                                {totalAdjustments !== 0 && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="text.primary">
                                                            Adjustments
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500} color={totalAdjustments >= 0 ? 'error.main' : 'success.main'}>
                                                            {totalAdjustments >= 0 ? '+' : ''}₹{totalAdjustments.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {totalPreviousDues > 0 && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="text.primary">
                                                            Previous dues
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500} color="warning.main">
                                                            ₹{totalPreviousDues.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2} mt={0.5} sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 0.75 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        Total billed
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                                                        ₹{totalBillAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Box>

                                    {/* ── Bill message (custom monthly note) ── */}
                                    {isWorking && (
                                        <Box sx={{ px: 1.5, pb: 1 }}>
                                            <Card variant="outlined">
                                                <Box display="flex" justifyContent="space-between" alignItems="center" px={1.5} py={0.75}>
                                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        Bill Message
                                                    </Typography>
                                                    {!notesEditing && (
                                                        <IconButton size="small" onClick={() => { setNotesDraft(currentPeriod?.f_notes || ''); setNotesEditing(true); }}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <Divider />
                                                <Box px={1.5} py={1}>
                                                    {notesEditing ? (
                                                        <Box display="flex" gap={1} alignItems="flex-start">
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                minRows={2}
                                                                size="small"
                                                                placeholder="e.g. Festival discount applied! Thank you for your patronage."
                                                                value={notesDraft}
                                                                onChange={e => setNotesDraft(e.target.value)}
                                                                inputProps={{ maxLength: 300 }}
                                                            />
                                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                                <IconButton size="small" color="primary" onClick={handleSaveNotes} disabled={notesSaving}>
                                                                    {notesSaving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => setNotesEditing(false)} disabled={notesSaving}>
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color={currentPeriod?.f_notes ? 'text.primary' : 'text.disabled'} sx={{ fontStyle: currentPeriod?.f_notes ? 'normal' : 'italic' }}>
                                                            {currentPeriod?.f_notes || 'No message set — tap edit to add one.'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Card>
                                        </Box>
                                    )}

                                    {/* ── Close / Lock month ── */}
                                    {isWorking && allInvoices.length > 0 && (
                                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                size="large"
                                                startIcon={<Lock />}
                                                onClick={() => setLockConfirmOpen(true)}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                Close &amp; finalise {MONTH_NAMES[currentPeriod.f_period_month - 1]}-{currentPeriod.f_period_year}
                                            </Button>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                                                Share invoices with customers after closing
                                            </Typography>
                                        </Box>
                                    )}
                                </Collapse>
                            </Card>
                        );
                    })()}

                    <Divider sx={{ mb: 2 }} />

                    {/* ── Bills / Invoices ── */}
                    <Box sx={{ mb: 2 }}>
                        {/* ── Search + Sort row ── */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <TextField
                                size="small"
                                placeholder="Search bills by customer name…"
                                value={billSearch}
                                onChange={e => setBillSearch(e.target.value)}
                                sx={{
                                    flex: '0 0 66%',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        border: '1.5px solid',
                                        borderColor: billSearch ? 'primary.main' : 'grey.200',
                                        transition: 'border-color 0.2s, box-shadow 0.2s',
                                        '& fieldset': { border: 'none' },
                                        '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                                        '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: billSearch ? 'primary.main' : 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: billSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setBillSearch('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                                <Select
                                    value={billSort}
                                    onChange={e => setBillSort(e.target.value)}
                                    displayEmpty
                                    renderValue={(val) => {
                                        const labels = { name: 'Name', amount_desc: 'Bill Amt ↓', amount_asc: 'Bill Amt ↑', due_desc: 'Total Due ↓', due_asc: 'Total Due ↑', status: 'Status' };
                                        return (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labels[val] || val}</Typography>
                                            </Box>
                                        );
                                    }}
                                    sx={{
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        border: '1.5px solid',
                                        borderColor: billSort !== 'name' ? 'primary.main' : 'grey.200',
                                        '& fieldset': { border: 'none' },
                                        '&:hover': { borderColor: 'primary.light' },
                                        '& .MuiSelect-select': { py: '6px' },
                                    }}
                                >
                                    <MenuItem value="name">Name</MenuItem>
                                    <MenuItem value="amount_desc">Bill Amt ↓</MenuItem>
                                    <MenuItem value="amount_asc">Bill Amt ↑</MenuItem>
                                    <MenuItem value="due_desc">Total Due ↓</MenuItem>
                                    <MenuItem value="due_asc">Total Due ↑</MenuItem>
                                    <MenuItem value="status">Status</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* ── Filter chips (area + adjustments + sent/not-sent) ── */}
                        {(() => {
                            const customerAreaMap = {};
                            customers.forEach(c => { if (c.f_area_name) customerAreaMap[c.f_id] = c.f_area_name; });
                            const areaNames = [...new Set(Object.values(customerAreaMap))].sort();
                            const adjCount = allInvoices.filter(inv => parseFloat(inv.f_adjustments_total || 0) !== 0).length;
                            const sentCount = isClosed ? allInvoices.filter(inv => (inv.f_sent || 0) > 0).length : 0;
                            const notSentCount = isClosed ? allInvoices.length - sentCount : 0;
                            const seenCount = isClosed ? allInvoices.filter(inv => (inv.f_seen || 0) > 0).length : 0;
                            // Not Seen = sent but not yet opened by the customer
                            const notSeenCount = isClosed ? allInvoices.filter(inv => (inv.f_sent || 0) > 0 && (inv.f_seen || 0) === 0).length : 0;
                            // Bills of customers with no phone: print or hand these over, not WhatsApp
                            const noPhoneIds = new Set(customers.filter(c => c.f_has_mobile === false).map(c => c.f_id));
                            const noPhoneCount = allInvoices.filter(inv => noPhoneIds.has(inv.f_business_customer_id)).length;
                            // Areas in route order; any area name not in the list (renamed or removed)
                            // still gets a slot rather than vanishing.
                            const routeNames = sortAreasByRoute(areas).map(a => a.f_name);
                            const orderedAreas = [...routeNames.filter(n => areaNames.includes(n)),
                                                  ...areaNames.filter(n => !routeNames.includes(n))];
                            const areaRow = (
                                <AreaFilterRow
                                    options={orderedAreas
                                        .map(name => ({
                                            key: name, label: name,
                                            count: allInvoices.filter(inv => customerAreaMap[inv.f_business_customer_id] === name).length,
                                        }))
                                        .filter(o => o.count > 0)}
                                    noArea={{ key: NO_AREA_FILTER,
                                              count: allInvoices.filter(inv => !customerAreaMap[inv.f_business_customer_id]).length }}
                                    total={allInvoices.length}
                                    value={billAreaFilter && (!billAreaFilter.startsWith('_') || billAreaFilter === NO_AREA_FILTER) ? billAreaFilter : ''}
                                    onChange={setBillAreaFilter}
                                    sx={{ mb: 1 }}
                                />
                            );
                            if (adjCount === 0 && sentCount === 0 && notSentCount === 0
                                && seenCount === 0 && notSeenCount === 0 && noPhoneCount === 0) return areaRow;
                            return (
                                <>
                                {areaRow}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                    <Chip
                                        label={`All (${allInvoices.length})`}
                                        size="small"
                                        color={!billAreaFilter ? 'primary' : 'default'}
                                        onClick={() => setBillAreaFilter('')}
                                        clickable
                                    />
                                    {adjCount > 0 && (
                                        <Chip
                                            label={`Adjustments (${adjCount})`}
                                            size="small"
                                            color={billAreaFilter === '_adjustments' ? 'warning' : 'default'}
                                            variant={billAreaFilter === '_adjustments' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_adjustments' ? '' : '_adjustments')}
                                            clickable
                                        />
                                    )}
                                    {sentCount > 0 && (
                                        <Chip
                                            label={`Sent (${sentCount})`}
                                            size="small"
                                            color={billAreaFilter === '_sent' ? 'success' : 'default'}
                                            variant={billAreaFilter === '_sent' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_sent' ? '' : '_sent')}
                                            clickable
                                        />
                                    )}
                                    {notSentCount > 0 && (
                                        <Chip
                                            label={`Not Sent (${notSentCount})`}
                                            size="small"
                                            color={billAreaFilter === '_not_sent' ? 'error' : 'default'}
                                            variant={billAreaFilter === '_not_sent' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_not_sent' ? '' : '_not_sent')}
                                            clickable
                                        />
                                    )}
                                    {seenCount > 0 && (
                                        <Chip
                                            label={`Seen (${seenCount})`}
                                            size="small"
                                            color={billAreaFilter === '_seen' ? 'info' : 'default'}
                                            variant={billAreaFilter === '_seen' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_seen' ? '' : '_seen')}
                                            clickable
                                        />
                                    )}
                                    {notSeenCount > 0 && (
                                        <Chip
                                            label={`Not Seen (${notSeenCount})`}
                                            size="small"
                                            color={billAreaFilter === '_not_seen' ? 'warning' : 'default'}
                                            variant={billAreaFilter === '_not_seen' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_not_seen' ? '' : '_not_seen')}
                                            clickable
                                        />
                                    )}
                                    {noPhoneCount > 0 && (
                                        <Chip
                                            label={`No phone (${noPhoneCount})`}
                                            size="small"
                                            color={billAreaFilter === '_no_phone' ? 'primary' : 'default'}
                                            variant={billAreaFilter === '_no_phone' ? 'filled' : 'outlined'}
                                            onClick={() => setBillAreaFilter(prev => prev === '_no_phone' ? '' : '_no_phone')}
                                            clickable
                                        />
                                    )}
                                </Box>
                                </>
                            );
                        })()}

                        {(() => {
                            const q = billSearch.trim().toLowerCase();
                            // Build area and door maps for filtering and display
                            const customerAreaMap = {};
                            const customerDoorMap = {};
                            customers.forEach(c => {
                                if (c.f_area_name) customerAreaMap[c.f_id] = c.f_area_name;
                                if (c.f_address_line1) customerDoorMap[c.f_id] = c.f_address_line1;
                            });

                            let filtered = allInvoices;
                            // Area / Adjustments / Sent filter
                            if (billAreaFilter === '_adjustments') {
                                filtered = filtered.filter(inv => parseFloat(inv.f_adjustments_total || 0) !== 0);
                            } else if (billAreaFilter === '_sent') {
                                filtered = filtered.filter(inv => (inv.f_sent || 0) > 0);
                            } else if (billAreaFilter === '_not_sent') {
                                filtered = filtered.filter(inv => (inv.f_sent || 0) === 0);
                            } else if (billAreaFilter === '_seen') {
                                filtered = filtered.filter(inv => (inv.f_seen || 0) > 0);
                            } else if (billAreaFilter === '_not_seen') {
                                filtered = filtered.filter(inv => (inv.f_sent || 0) > 0 && (inv.f_seen || 0) === 0);
                            } else if (billAreaFilter === '_no_phone') {
                                const noPhone = new Set(customers.filter(c => c.f_has_mobile === false).map(c => c.f_id));
                                filtered = filtered.filter(inv => noPhone.has(inv.f_business_customer_id));
                            } else if (billAreaFilter === NO_AREA_FILTER) {
                                filtered = filtered.filter(inv => !customerAreaMap[inv.f_business_customer_id]);
                            } else if (billAreaFilter) {
                                filtered = filtered.filter(inv => customerAreaMap[inv.f_business_customer_id] === billAreaFilter);
                            }
                            // Search filter
                            if (q) {
                                filtered = filtered.filter(inv => inv.customer_name?.toLowerCase().includes(q));
                            }
                            // Sort
                            const statusOrder = { unpaid: 0, partial: 1, paid: 2 };
                            filtered = [...filtered].sort((a, b) => {
                                if (billSort === 'name') return (a.customer_name || '').localeCompare(b.customer_name || '');
                                if (billSort === 'amount_desc') return parseFloat(b.f_total_amount) - parseFloat(a.f_total_amount);
                                if (billSort === 'amount_asc') return parseFloat(a.f_total_amount) - parseFloat(b.f_total_amount);
                                if (billSort === 'due_desc' || billSort === 'due_asc') {
                                    const dueA = parseFloat(a.balance_due || 0) + parseFloat(a.prior_balance_due || 0);
                                    const dueB = parseFloat(b.balance_due || 0) + parseFloat(b.prior_balance_due || 0);
                                    return billSort === 'due_desc' ? dueB - dueA : dueA - dueB;
                                }
                                if (billSort === 'status') return (statusOrder[a.payment_status] ?? 3) - (statusOrder[b.payment_status] ?? 3);
                                return 0;
                            });

                            if (allInvoices.length === 0) {
                                const _now = new Date();
                                const isFuturePeriod = currentPeriod && (
                                    currentPeriod.f_period_year > _now.getFullYear() ||
                                    (currentPeriod.f_period_year === _now.getFullYear() && currentPeriod.f_period_month > _now.getMonth() + 1)
                                );
                                return (
                                <Box>
                                    <Card variant="outlined" sx={{ mb: 1.5 }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center' }}>
                                                {isFuturePeriod ? 'Bills cannot be created for a future month' : 'No bill created yet for this month'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    {isWorking && !isFuturePeriod && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                startIcon={genLoading ? <CircularProgress size={18} color="inherit" /> : <Receipt />}
                                                onClick={() => handleGenerateInvoice(false)}
                                                disabled={genLoading}
                                            >
                                                Create bill for this month ({MONTH_NAMES[currentPeriod.f_period_month - 1].slice(0, 3)}-{currentPeriod.f_period_year})
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                fullWidth
                                                size="large"
                                                startIcon={<Lock />}
                                                onClick={() => setLockConfirmOpen(true)}
                                            >
                                                Close month without bills ({MONTH_NAMES[currentPeriod.f_period_month - 1].slice(0, 3)}-{currentPeriod.f_period_year})
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                );
                            }

                            const visibleInvoices = filtered.slice(0, billVisibleCount);
                            const hasMoreInvoices = filtered.length > billVisibleCount;
                            return (
                            <>
                                {visibleInvoices.map((inv) => {
                                    const isExpanded = expandedInvoiceId === inv.f_id;
                                    return (
                                        <Card
                                            key={inv.f_id}
                                            variant="outlined"
                                            sx={{ mb: 1, cursor: 'pointer', borderColor: isExpanded ? 'primary.main' : 'divider' }}
                                            onClick={() => handleExpandInvoice(inv.f_id)}
                                        >
                                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                                                {/* Row 1: Customer name + door/area */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                            {inv.customer_name}
                                                        </Typography>
                                                        {(() => {
                                                            const door = customerDoorMap[inv.f_business_customer_id];
                                                            const area = customerAreaMap[inv.f_business_customer_id];
                                                            const info = [door, area].filter(Boolean).join(' · ');
                                                            return info ? (
                                                                <Typography variant="caption" color="text.secondary">{info}</Typography>
                                                            ) : null;
                                                        })()}
                                                    </Box>
                                                </Box>

                                                {/* Row 2: Bill/Due + Share buttons + Expand */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3, whiteSpace: 'nowrap' }}>
                                                            <Typography sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 700 }}>Bill</Typography>
                                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>₹{parseFloat(inv.f_total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                                        </Box>
                                                        {(() => {
                                                            const totalDue = parseFloat(inv.f_total_amount) + parseFloat(inv.f_previous_due || 0) - parseFloat(inv.paid_amount ?? 0);
                                                            return totalDue !== parseFloat(inv.f_total_amount) ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3, whiteSpace: 'nowrap' }}>
                                                                    <Typography sx={{ fontSize: '0.6rem', color: 'error.main', fontWeight: 700 }}>Due</Typography>
                                                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'error.main' }}>₹{totalDue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                                                </Box>
                                                            ) : null;
                                                        })()}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {isClosed && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                                                <ShareButtons
                                                                    phone={getCardShareData(inv).custPhone}
                                                                    noPhone={getCardShareData(inv).noPhone}
                                                                    text={getCardShareData(inv).waMsg}
                                                                    getShareUrl={async () => {
                                                                        const res = await invoiceAPI.getInvoiceShareUrl(businessId, inv.f_id);
                                                                        return res.data?.share_url;
                                                                    }}
                                                                    onView={() => setViewInvoiceId(inv.f_id)}
                                                                    size="sm"
                                                                    label={false}
                                                                />
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Expanded actions */}
                                                <Collapse in={isExpanded} unmountOnExit>
                                                    <Box
                                                        sx={{ pt: 1.5, mt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {/* Bill breakdown */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">This month's bill</Typography>
                                                            <Typography variant="body2" fontWeight="bold">₹{parseFloat(inv.f_total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                                        </Box>
                                                        {parseFloat(inv.f_previous_due) > 0 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="body2" color="text.secondary">Arrears (prior months)</Typography>
                                                                <Typography variant="body2" color="warning.main" fontWeight="bold">
                                                                    +₹{parseFloat(inv.f_previous_due).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {parseFloat(inv.f_previous_due) > 0 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                <Typography variant="body2" fontWeight="bold">Total payable</Typography>
                                                                <Typography variant="body2" fontWeight="bold" color="error.main">
                                                                    ₹{(parseFloat(inv.f_total_amount) + parseFloat(inv.f_previous_due)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Paid so far</Typography>
                                                            <Typography variant="body2" color="success.main" fontWeight="bold">₹{parseFloat(inv.paid_amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Balance due</Typography>
                                                            <Typography variant="body2" color="error.main" fontWeight="bold">₹{(parseFloat(inv.f_total_amount) + parseFloat(inv.f_previous_due || 0) - parseFloat(inv.paid_amount ?? 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                                        </Box>

                                                        {/* ── Adjustments ── */}
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                    Adjustments
                                                                </Typography>
                                                                {isWorking && (
                                                                    <IconButton size="small" onClick={() => setAdjFormOpen(adjFormOpen === inv.f_id ? null : inv.f_id)}>
                                                                        <Add fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </Box>

                                                            {/* Existing adjustments */}
                                                            {(invoiceAdjustments[inv.f_id] || []).length === 0 && adjFormOpen !== inv.f_id && (
                                                                <Typography variant="caption" color="text.disabled">No adjustments yet</Typography>
                                                            )}
                                                            {(invoiceAdjustments[inv.f_id] || []).map(adj => (
                                                                <Box key={adj.f_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                                    <Box>
                                                                        <Typography variant="caption" fontWeight={500}>{adjTypeLabels[adj.f_adjustment_type] || adj.f_adjustment_type}</Typography>
                                                                        {adj.f_adjustment_description && (
                                                                            <Typography variant="caption" color="text.secondary"> — {adj.f_adjustment_description}</Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Typography variant="caption" fontWeight="bold"
                                                                            color={parseFloat(adj.f_adjustment_amount) >= 0 ? 'error.main' : 'success.main'}>
                                                                            {parseFloat(adj.f_adjustment_amount) >= 0 ? '+' : ''}₹{parseFloat(adj.f_adjustment_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                                        </Typography>
                                                                        {isWorking && (
                                                                            <IconButton size="small" onClick={() => handleDeleteAdjustment(inv.f_id, adj.f_id)}>
                                                                                <Delete fontSize="small" color="action" />
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            ))}

                                                            {/* Add adjustment form */}
                                                            {adjFormOpen === inv.f_id && (
                                                                <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                                                                        <InputLabel>Type</InputLabel>
                                                                        <Select
                                                                            label="Type"
                                                                            value={adjFormData.type}
                                                                            onChange={e => setAdjFormData(d => ({ ...d, type: e.target.value }))}
                                                                        >
                                                                            <MenuItem value="Discount">Discount</MenuItem>
                                                                            <MenuItem value="OldDues">Old Dues</MenuItem>
                                                                            <MenuItem value="OtherCharges">Other Charges</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                    <TextField
                                                                        size="small" fullWidth sx={{ mb: 1 }}
                                                                        label="Amount"
                                                                        type="number"
                                                                        value={adjFormData.amount}
                                                                        onChange={e => setAdjFormData(d => ({ ...d, amount: e.target.value }))}
                                                                    />
                                                                    <TextField
                                                                        size="small" fullWidth sx={{ mb: 1 }}
                                                                        placeholder="Description (optional)"
                                                                        value={adjFormData.description}
                                                                        onChange={e => setAdjFormData(d => ({ ...d, description: e.target.value }))}
                                                                    />
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <Button
                                                                            size="small" variant="contained"
                                                                            disabled={adjLoading || !adjFormData.amount}
                                                                            onClick={() => handleAddAdjustment(inv.f_id)}
                                                                            startIcon={adjLoading ? <CircularProgress size={14} color="inherit" /> : null}
                                                                        >
                                                                            Add
                                                                        </Button>
                                                                        <Button size="small" onClick={() => setAdjFormOpen(null)}>Cancel</Button>
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                fullWidth
                                                                startIcon={<Receipt />}
                                                                onClick={() => setViewInvoiceId(inv.f_id)}
                                                            >
                                                                View bill
                                                            </Button>
                                                        </Box>

                                                    </Box>
                                                </Collapse>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {hasMoreInvoices && (
                                    <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                                        <Button variant="outlined" size="small" onClick={() => setBillVisibleCount(v => v + 50)}>
                                            Load more ({filtered.length - billVisibleCount} remaining)
                                        </Button>
                                    </Box>
                                )}

                            </>
                            );
                        })()}
                    </Box>
                </>
            )}

            {/* ── Subscription Transactions Dialog ── */}
            <Dialog
                open={subDialogOpen}
                onClose={() => setSubDialogOpen(false)}
                {...APP_DIALOG}
            >
                <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Regular Deliveries
                    <IconButton onClick={() => setSubDialogOpen(false)} aria-label="Close"><Close /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {subTxns.map((txn) => (
                                    <TableRow key={txn.f_id}>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{customerName(txn.f_business_customer_id)}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{productName(txn.f_business_product_id)}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(txn.f_time)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{txn.f_quantity}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>₹{txn.f_total_sale_price_after_discount}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            {/* Shared invoice viewer — same server-rendered page that gets shared */}
            <InvoiceViewDialog businessId={businessId} invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />



            {/* ── Delete confirm ── */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove this entry?</DialogTitle>
                <DialogContent>
                    <Typography>This extra delivery will be removed from the bill.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button onClick={() => handleDeleteTxn(deleteConfirm)} variant="contained" color="error">Remove</Button>
                </DialogActions>
            </Dialog>

            {/* ── Lock / Close month confirm ── */}
            <Dialog open={lockConfirmOpen} onClose={() => setLockConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="warning" /> Close this month?
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Once closed, <strong>no changes</strong> can be made to{' '}
                        {currentPeriod ? periodLabel(currentPeriod) : 'this month'}'s bill.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Make sure all deliveries are recorded and the bill amount is correct before closing.
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>
                        After closing, share the invoices with your customers. You can start the next month whenever you're ready.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLockConfirmOpen(false)}>Not yet</Button>
                    <Button
                        onClick={handleLock}
                        variant="contained"
                        color="warning"
                        disabled={lockLoading}
                    >
                        {lockLoading ? <CircularProgress size={20} /> : 'Yes, close it'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Unlock / Reopen confirm with cascade warning ── */}
            <Dialog open={unlockConfirmOpen} onClose={() => setUnlockConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <Warning color="error" /> Reopen {currentPeriod ? periodLabel(currentPeriod) : 'this month'}?
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        This will make {currentPeriod ? periodLabel(currentPeriod) : 'this month'} editable again.
                    </Typography>

                    {futurePeriods.length > 0 ? (
                        <>
                            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                                <strong>All future months will be wiped:</strong>
                            </Alert>
                            {futurePeriods.map(fp => (
                                <Box key={fp.f_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                    <Typography variant="body2">{periodLabel(fp)}</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {fp.invoice_count > 0 && (
                                            <Chip size="small" label={`${fp.invoice_count} bill(s)`} color="error" variant="outlined" />
                                        )}
                                        {(fp.txn_count_subscription + fp.txn_count_manual) > 0 && (
                                            <Chip size="small" label={`${fp.txn_count_subscription + fp.txn_count_manual} txn(s)`} color="warning" variant="outlined" />
                                        )}
                                        {fp.adjustment_count > 0 && (
                                            <Chip size="small" label={`${fp.adjustment_count} adj kept`} color="info" variant="outlined" />
                                        )}
                                    </Box>
                                </Box>
                            ))}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Transactions and bills will be deleted. Adjustments are kept.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No future months exist. Only this month will be reopened.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnlockConfirmOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleUnlock}
                        variant="contained"
                        color="error"
                        disabled={unlockLoading}
                    >
                        {unlockLoading ? <CircularProgress size={20} /> : 'Yes, reopen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BillingPage;
